import React, { useState } from "react";
import axios from "axios";
import { Star, X } from "lucide-react";
import Env from "../runtimeEnv";

const SessionFeedbackModal = ({ isOpen, onClose }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const submit = async (event) => {
    event.preventDefault();
    if (!rating) return;
    setStatus("sending");
    setError("");
    try {
      await axios.post(
        `${Env.REACT_APP_PYTHON_API_URL}/api/session-feedback`,
        { rating, message: message.trim() },
        { withCredentials: true }
      );
      setStatus("sent");
    } catch (requestError) {
      setStatus("error");
      setError(requestError.response?.data?.message || "We couldn't send your feedback. Please try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="feedback-title" className="w-full max-w-xl rounded-2xl bg-white p-7 shadow-2xl sm:p-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="feedback-title" className="text-3xl font-bold text-[#171b3a]">Share your feedback</h2>
            <p className="mt-2 text-gray-600">How was your therapy session? Your feedback helps us improve.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close feedback form" className="rounded-full p-2 text-gray-500 hover:bg-gray-100"><X /></button>
        </div>

        {status === "sent" ? (
          <div className="mt-8 text-center">
            <p className="rounded-xl bg-green-50 p-5 text-green-800">Thank you for helping us improve.</p>
            <button type="button" onClick={onClose} className="mt-6 rounded-full bg-[#22AAEE] px-12 py-3 font-semibold text-white">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8">
            <fieldset>
              <legend className="font-semibold text-[#171b3a]">How was your session?</legend>
              <div className="mt-4 flex justify-center gap-4 sm:gap-7">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button key={value} type="button" aria-label={`${value} star${value > 1 ? "s" : ""}`} onMouseEnter={() => setHoveredRating(value)} onMouseLeave={() => setHoveredRating(0)} onClick={() => setRating(value)} className="text-[#22AAEE]">
                    <Star className="h-11 w-11" fill={value <= (hoveredRating || rating) ? "currentColor" : "none"} />
                  </button>
                ))}
              </div>
              <p className="mt-3 text-center text-sm text-gray-500">Click a star to rate your experience</p>
            </fieldset>

            <label htmlFor="session-feedback-message" className="mt-8 block font-semibold text-[#171b3a]">Tell us more (optional)</label>
            <textarea id="session-feedback-message" rows="5" maxLength="1000" value={message} onChange={(event) => setMessage(event.target.value)} placeholder="Share what went well or what we could improve…" className="mt-3 w-full resize-y rounded-xl border border-gray-300 p-4 focus:border-[#22AAEE] focus:outline-none focus:ring-2 focus:ring-[#22AAEE]/30" />
            <div className="text-right text-sm text-gray-500">{message.length}/1000</div>
            {error && <p role="alert" className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
            <div className="mt-7 text-center">
              <button type="submit" disabled={!rating || status === "sending"} className="w-full max-w-sm rounded-full bg-[#22AAEE] px-10 py-3 font-semibold text-white hover:bg-[#168dcc] disabled:cursor-not-allowed disabled:opacity-50">
                {status === "sending" ? "Submitting…" : "Submit feedback"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SessionFeedbackModal;
